import React from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Plus, Trash2 } from 'lucide-react';

// Re-usable Betaalplan (payment plan) editor for the admin quote/invoice form.
// Stores the plan as: { enabled: bool, installments: [{pct, label, due_at}] }.
// Default preset: 30% aanbetaling / 50% bij 70% werk / 20% bij oplevering.

export const DEFAULT_PAYMENT_PLAN = {
  enabled: true,
  installments: [
    { pct: 30, label: 'Aanbetaling',   due_at: 'voor aanvang van de werkzaamheden' },
    { pct: 50, label: 'Tussenfactuur', due_at: 'bij 70% van het werk' },
    { pct: 20, label: 'Slottermijn',   due_at: 'bij oplevering' },
  ],
};

export const PRESETS = {
  '30/50/20': DEFAULT_PAYMENT_PLAN.installments,
  '50/50':   [
    { pct: 50, label: 'Aanbetaling', due_at: 'voor aanvang van de werkzaamheden' },
    { pct: 50, label: 'Slottermijn', due_at: 'bij oplevering' },
  ],
  '100% bij oplevering': [
    { pct: 100, label: 'Volledig bedrag', due_at: 'bij oplevering' },
  ],
};

export const PaymentPlanEditor = ({ value, onChange, totalIncl = 0 }) => {
  const plan = value || { enabled: false, installments: [] };
  const total = (plan.installments || []).reduce((a, b) => a + (Number(b.pct) || 0), 0);
  const isValid = Math.abs(total - 100) < 0.01;
  const [expanded, setExpanded] = React.useState(false);

  const set = (next) => onChange(next);

  const toggle = (on) => {
    if (on && (!plan.installments || plan.installments.length === 0)) {
      set({ enabled: true, installments: [...DEFAULT_PAYMENT_PLAN.installments] });
      setExpanded(true);
    } else {
      set({ ...plan, enabled: on });
    }
  };

  const usePreset = (key) => {
    set({ enabled: true, installments: PRESETS[key].map(x => ({ ...x })) });
  };

  const updateInstallment = (i, key, val) => {
    const list = [...(plan.installments || [])];
    list[i] = { ...list[i], [key]: key === 'pct' ? Number(val) || 0 : val };
    set({ ...plan, installments: list });
  };

  // Update pct based on a bedrag (€) input — reverse calc: pct = (bedrag / totalIncl) * 100.
  // Stores 4 decimals so the round-trip 2000 → pct → 2000.00 stays lossless (avoids drift).
  const updateInstallmentByAmount = (i, eurStr) => {
    const list = [...(plan.installments || [])];
    const bedrag = Number(eurStr) || 0;
    const newPct = totalIncl > 0 ? Math.round((bedrag / totalIncl) * 1000000) / 10000 : 0;
    list[i] = { ...list[i], pct: newPct };
    set({ ...plan, installments: list });
  };

  const addInstallment = () => {
    const list = [...(plan.installments || [])];
    list.push({ pct: 0, label: 'Termijn', due_at: '' });
    set({ ...plan, installments: list });
  };

  const removeInstallment = (i) => {
    const list = [...(plan.installments || [])];
    list.splice(i, 1);
    set({ ...plan, installments: list });
  };

  // Compact summary string: "30/50/20%"
  const summary = (plan.installments || []).map(x => `${x.pct}`).join('/') + '%';

  const fmtEur = (n) => (Number(n) || 0).toFixed(2);

  return (
    <div className="p-2.5 bg-gray-50 rounded border border-gray-200 text-sm" data-testid="payment-plan-editor">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Switch checked={!!plan.enabled} onCheckedChange={toggle} id="pp-enabled" data-testid="payment-plan-enabled" />
          <Label htmlFor="pp-enabled" className="text-gray-800 font-semibold cursor-pointer text-xs whitespace-nowrap">Betaalplan</Label>
          {plan.enabled && (
            <span className={`text-xs font-mono ${isValid ? 'text-green-700' : 'text-red-600'}`} title={isValid ? 'OK' : 'Som moet 100% zijn'}>
              {summary}
            </span>
          )}
        </div>
        {plan.enabled && (
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="text-xs text-[#3da5a5] hover:underline whitespace-nowrap"
            data-testid="payment-plan-toggle"
          >
            {expanded ? 'Verbergen' : 'Bewerken'}
          </button>
        )}
      </div>

      {plan.enabled && expanded && (
        <div className="mt-3 space-y-2">
          <div className="flex gap-1 flex-wrap">
            {Object.keys(PRESETS).map((k) => (
              <Button key={k} type="button" size="sm" variant="outline" className="text-[10px] h-6 px-2" onClick={() => usePreset(k)} data-testid={`payment-plan-preset-${k.replace(/[^a-z0-9]/gi, '-')}`}>{k}</Button>
            ))}
          </div>
          <div className="grid grid-cols-12 gap-1.5 items-center text-[10px] font-semibold text-gray-500 px-1">
            <div className="col-span-2">%</div>
            <div className="col-span-2">Bedrag (€)</div>
            <div className="col-span-3">Termijn</div>
            <div className="col-span-4">Te betalen</div>
            <div className="col-span-1"></div>
          </div>
          <div className="space-y-1.5">
            {(plan.installments || []).map((inst, i) => (
              <InstallmentRow
                key={i}
                inst={inst}
                i={i}
                totalIncl={totalIncl}
                onUpdate={updateInstallment}
                onUpdateByAmount={updateInstallmentByAmount}
                onRemove={removeInstallment}
              />
            ))}
          </div>
          <div className="flex items-center justify-between">
            <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={addInstallment} data-testid="payment-plan-add">
              <Plus className="w-3.5 h-3.5 mr-1" /> Termijn
            </Button>
            <p className={`text-xs font-semibold ${isValid ? 'text-green-600' : 'text-red-600'}`}>
              Totaal: {total.toFixed(2)}% {isValid ? '✓' : '(moet 100%)'}
              {totalIncl > 0 && <span className="text-gray-500 font-normal ml-2">· van € {fmtEur(totalIncl)}</span>}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// Single row of the betaalplan editor. Owns LOCAL string state for both inputs
// so the user can type freely (no controlled-input drift / cursor jumps).
// Only syncs from props when NOT focused (i.e., when an EXTERNAL change happens
// like a preset click or pct edit on another row).
function InstallmentRow({ inst, i, totalIncl, onUpdate, onUpdateByAmount, onRemove }) {
  const fmtEur = (n) => (Number(n) || 0).toFixed(2);
  const fmtPct = (n) => {
    const v = Number(n) || 0;
    const s = v.toFixed(2);
    return s.replace(/\.?0+$/, '') || '0';
  };
  const computedBedrag = totalIncl > 0 ? (totalIncl * (Number(inst.pct) || 0) / 100) : 0;
  const [bedragStr, setBedragStr] = React.useState(computedBedrag > 0 ? fmtEur(computedBedrag) : '');
  const [pctStr, setPctStr] = React.useState(fmtPct(inst.pct));
  const [editingBedrag, setEditingBedrag] = React.useState(false);
  const [editingPct, setEditingPct] = React.useState(false);

  // Sync local strings from props ONLY when user is NOT editing that field.
  // (Intentionally excluding editingBedrag/editingPct/computedBedrag from deps so
  // the user's keystrokes are not interrupted while they type.)
  React.useEffect(() => {
    if (!editingBedrag) {
      setBedragStr(computedBedrag > 0 ? fmtEur(computedBedrag) : '');
    }
    if (!editingPct) {
      setPctStr(fmtPct(inst.pct));
    }
  }, [inst.pct, totalIncl]);

  return (
    <div className="grid grid-cols-12 gap-1.5 items-center" data-testid={`payment-plan-row-${i}`}>
      <div className="col-span-2">
        <Input
          type="number" min="0" max="100" step="0.01"
          value={pctStr}
          onFocus={() => setEditingPct(true)}
          onBlur={() => setEditingPct(false)}
          onChange={(e) => { setPctStr(e.target.value); onUpdate(i, 'pct', e.target.value); }}
          className="bg-white h-7 text-xs px-1.5"
          placeholder="%"
          data-testid={`payment-plan-pct-${i}`}
        />
      </div>
      <div className="col-span-2">
        <Input
          type="number" min="0" step="0.01"
          value={bedragStr}
          onFocus={() => setEditingBedrag(true)}
          onBlur={() => setEditingBedrag(false)}
          onChange={(e) => { setBedragStr(e.target.value); onUpdateByAmount(i, e.target.value); }}
          disabled={!(totalIncl > 0)}
          className="bg-white h-7 text-xs px-1.5"
          placeholder="€"
          title={totalIncl > 0 ? 'Bedrag (€) — % wordt automatisch berekend' : 'Eerst items invullen'}
          data-testid={`payment-plan-amount-${i}`}
        />
      </div>
      <div className="col-span-3">
        <Input value={inst.label} onChange={(e) => onUpdate(i, 'label', e.target.value)} className="bg-white h-7 text-xs" placeholder="Aanbetaling" data-testid={`payment-plan-label-${i}`} />
      </div>
      <div className="col-span-4">
        <Input value={inst.due_at} onChange={(e) => onUpdate(i, 'due_at', e.target.value)} className="bg-white h-7 text-xs" placeholder="bij oplevering" data-testid={`payment-plan-due-${i}`} />
      </div>
      <div className="col-span-1 flex justify-center">
        <Button type="button" size="sm" variant="ghost" className="text-red-500 h-7 w-7 p-0" onClick={() => onRemove(i)} data-testid={`payment-plan-remove-${i}`}>
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}

// Read-only display of the betaalplan inside a PDF/Card.
// Renders in inline-style so it works inside html2pdf-printed containers.
// Forces a NEW PAGE in the PDF and renders that page as a STAND-ALONE document
// with its own header (logo + bedrijfsnaam) and footer (KvK / IBAN / BIC) —
// matching the look of page 1 for a professional appearance.
const COMPANY = {
  name: 'Cardoso Bouw en Onderhoud',
  address: 'Vuntuslaan 69',
  city: '1231 NR Loosdrecht',
  phone: '0685071009',
  email: 'info@cardosobouw.nl',
  kvk: '85310646',
  btw_id: 'NL004077138B58',
  iban: 'NL33INGB0709828160',
  bic: 'INGBNL2A',
};

export const PaymentPlanBlock = ({ plan, totalIncl, documentNumber, documentType = 'Offerte', currency = '€' }) => {
  if (!plan || !plan.enabled || !plan.installments?.length) return null;
  const fmt = (v) => `${currency} ${(Number(v) || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return (
    <>
      <div className="html2pdf__page-break" style={{ pageBreakBefore: 'always', breakBefore: 'page', height: 0 }} />
      <div style={{ fontFamily: 'Arial, sans-serif', padding: '40px', maxWidth: '800px', margin: '0 auto', color: '#333', background: 'white', minHeight: '1000px', display: 'flex', flexDirection: 'column' }} data-testid="payment-plan-block">

        {/* Page-2 header — matches page-1 header style */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '30px' }}>
          <div>
            <h2 style={{ color: '#1a5276', fontSize: '22px', margin: '0 0 8px 0' }}>Betalingsplan</h2>
            {documentNumber && (
              <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>
                Behorend bij {documentType} <strong>{documentNumber}</strong>
              </p>
            )}
          </div>
          <div style={{ textAlign: 'right' }}>
            <img src="/logo-cardoso.jpeg" alt="Logo" style={{ width: '120px', marginBottom: '10px' }} onError={(e) => { e.target.style.display = 'none'; }} />
            <p style={{ margin: '2px 0', fontSize: '12px' }}>{COMPANY.name}</p>
            <p style={{ margin: '2px 0', fontSize: '12px' }}>{COMPANY.address}</p>
            <p style={{ margin: '2px 0', fontSize: '12px' }}>{COMPANY.city}</p>
          </div>
        </div>

        {/* Main content */}
        <div style={{ flex: 1 }}>
          <p style={{ fontSize: '13px', color: '#5d4037', margin: '0 0 20px 0' }}>
            Wij hanteren een gefaseerd betalingsplan voor deze opdracht. De betalingen worden als volgt verdeeld:
          </p>

          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', marginBottom: '20px' }}>
            <thead>
              <tr style={{ background: '#1a5276', color: 'white' }}>
                <th style={{ padding: '10px 8px', textAlign: 'left' }}>%</th>
                <th style={{ padding: '10px 8px', textAlign: 'left' }}>Termijn</th>
                <th style={{ padding: '10px 8px', textAlign: 'left' }}>Te betalen</th>
                <th style={{ padding: '10px 8px', textAlign: 'left' }}>Status</th>
                {totalIncl != null && <th style={{ padding: '10px 8px', textAlign: 'right' }}>Bedrag (incl. BTW)</th>}
              </tr>
            </thead>
            <tbody>
              {plan.installments.map((inst, i) => {
                const isPaid = !!inst.paid_at;
                return (
                  <tr key={i} style={{ borderBottom: '1px solid #eee', background: isPaid ? '#e8f8f5' : 'transparent' }}>
                    <td style={{ padding: '10px 8px', fontWeight: 'bold' }}>{inst.pct}%</td>
                    <td style={{ padding: '10px 8px' }}>{inst.label}</td>
                    <td style={{ padding: '10px 8px', color: '#666' }}>{inst.due_at}</td>
                    <td style={{ padding: '10px 8px', fontSize: '12px', color: isPaid ? '#0a7a0a' : '#7e5109', fontWeight: isPaid ? 'bold' : 'normal' }}>
                      {isPaid
                        ? `✓ Betaald${inst.invoice_number ? ` (factuur ${inst.invoice_number})` : ''}`
                        : (inst.invoice_number ? `Gefactureerd: ${inst.invoice_number}` : 'Nog te factureren')}
                    </td>
                    {totalIncl != null && <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 'bold' }}>{fmt((Number(totalIncl) || 0) * (Number(inst.pct) || 0) / 100)}</td>}
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div style={{ padding: '15px', background: '#fef9e7', border: '1px solid #f4d03f', borderRadius: '4px', fontSize: '12px', color: '#5d4037', marginBottom: '20px' }}>
            <strong>Toelichting:</strong> bedragen op deze pagina zijn inclusief BTW en gebaseerd op het totaalbedrag van het hoofddocument.
            Betalingen kunnen worden voldaan via iDEAL/Bancontact (via de betaallink in uw e-mail) of per bankoverschrijving op IBAN <strong>{COMPANY.iban}</strong> t.n.v. {COMPANY.name}.
          </div>
        </div>

        {/* Page-2 footer — same identification as page 1 */}
        <div style={{ borderTop: '1px solid #ddd', paddingTop: '15px', marginTop: 'auto', fontSize: '11px', color: '#666', display: 'flex', justifyContent: 'space-between' }}>
          <div>
            <p style={{ margin: '2px 0' }}>{COMPANY.name} · {COMPANY.address}, {COMPANY.city}</p>
            <p style={{ margin: '2px 0' }}>Mobiel: {COMPANY.phone} · E-mail: {COMPANY.email}</p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <p style={{ margin: '2px 0' }}>KVK: {COMPANY.kvk} · BTW-id: {COMPANY.btw_id}</p>
            <p style={{ margin: '2px 0' }}>IBAN: {COMPANY.iban} · BIC: {COMPANY.bic}</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default PaymentPlanEditor;

// Action panel: shown in admin Quotes list for offertes met betaalplan.
// Logica:
//   - Niet-laatste termijnen → "Markeer als betaald" (manueel — geen aparte factuur)
//   - Laatste termijn (Eindbetaling) → "Eindfactuur maken" — creëert de echte factuur
//   - Reeds betaalde termijnen → groene ✓ badge
//   - Termijnen met factuur → blauwe badge met factuurnr
export const InstallmentInvoiceActions = ({ quote, axiosInstance, onRefresh }) => {
  const plan = quote?.payment_plan;
  const [busy, setBusy] = React.useState(null);
  if (!plan?.enabled || !plan.installments?.length) return null;

  const lastIndex = plan.installments.length - 1;

  const callApi = async (method, path, label) => {
    try {
      const r = await axiosInstance({ method, url: path });
      try {
        const { toast } = await import('sonner');
        toast.success(`${label} uitgevoerd${r.data?.number ? ` — factuur ${r.data.number}` : ''}`);
      } catch { /* sonner not available */ }
      onRefresh && onRefresh();
    } catch (e) {
      try {
        const { toast } = await import('sonner');
        toast.error(e.response?.data?.detail || `${label} mislukt`);
      } catch { alert(e.response?.data?.detail || `${label} mislukt`); }
    }
  };

  const markPaid = async (i) => {
    const note = window.prompt(`Hoe is deze termijn betaald? (bv. Bankoverschrijving 26-05-2026)\nLaat leeg om alleen als betaald te markeren.`, '');
    if (note === null) return;
    setBusy(`paid-${i}`);
    try {
      await axiosInstance.post(`/quotes/${quote.id}/installments/${i}/mark-paid`, { note: note || null, paid_via: 'handmatig' });
      try {
        const { toast } = await import('sonner');
        toast.success(`Termijn ${i + 1} gemarkeerd als betaald`);
      } catch { /* */ }
      onRefresh && onRefresh();
    } catch (e) {
      try {
        const { toast } = await import('sonner');
        toast.error(e.response?.data?.detail || 'Mislukt');
      } catch { alert(e.response?.data?.detail || 'Mislukt'); }
    } finally {
      setBusy(null);
    }
  };

  const unmarkPaid = async (i) => {
    if (!window.confirm(`Markering "betaald" ongedaan maken voor termijn ${i + 1}?`)) return;
    setBusy(`unpaid-${i}`);
    await callApi('post', `/quotes/${quote.id}/installments/${i}/unmark-paid`, 'Markering ongedaan');
    setBusy(null);
  };

  const createEindfactuur = async (i) => {
    if (!window.confirm(`Eindfactuur aanmaken voor termijn ${i + 1} (${plan.installments[i].label})?\n\nDe factuur toont automatisch dat eerdere termijnen al betaald zijn.`)) return;
    setBusy(`inv-${i}`);
    try {
      const r = await axiosInstance.post(`/quotes/${quote.id}/installments/${i}/create-invoice`);
      try {
        const { toast } = await import('sonner');
        toast.success(`Eindfactuur ${r.data.number} aangemaakt`);
      } catch { /* */ }
      onRefresh && onRefresh();
    } catch (e) {
      try {
        const { toast } = await import('sonner');
        toast.error(e.response?.data?.detail || 'Aanmaken mislukt');
      } catch { /* */ }
    } finally {
      setBusy(null);
    }
  };

  const sendPaymentRequest = async (i) => {
    const inst = plan.installments[i];
    const klantEmail = quote.client_email || '';
    if (!klantEmail) {
      try {
        const { toast } = await import('sonner');
        toast.error('Klant heeft geen e-mailadres in deze offerte');
      } catch { alert('Klant heeft geen e-mailadres'); }
      return;
    }
    if (!window.confirm(`Betalingsverzoek versturen voor termijn ${i + 1} (${inst.label}) naar ${klantEmail}?\n\nDe klant ontvangt een e-mail met iDEAL-link. Wanneer betaald, wordt deze termijn automatisch op ✓ Betaald gezet — er wordt GEEN aparte factuur gemaakt.`)) return;
    setBusy(`req-${i}`);
    try {
      const r = await axiosInstance.post(`/quotes/${quote.id}/installments/${i}/send-payment-request`);
      try {
        const { toast } = await import('sonner');
        toast.success(`Betalingsverzoek verstuurd naar ${r.data.sent_to} — €${r.data.amount}`);
      } catch { /* */ }
      onRefresh && onRefresh();
    } catch (e) {
      try {
        const { toast } = await import('sonner');
        toast.error(e.response?.data?.detail || 'Versturen mislukt');
      } catch { alert(e.response?.data?.detail || 'Mislukt'); }
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mt-3 p-3 rounded border border-amber-200 bg-amber-50/40" data-testid={`installment-actions-${quote.id}`}>
      <p className="text-xs font-semibold text-amber-900 mb-2">Betaalplan — voortgang & eindfactuur</p>
      <div className="space-y-1.5">
        {plan.installments.map((inst, i) => {
          const isLast = i === lastIndex;
          const isPaid = !!inst.paid_at;
          const hasInvoice = !!inst.invoice_id || !!inst.invoice_number;
          const requestSent = !!inst.payment_request_sent_at;
          return (
            <div key={i} className="flex items-center justify-between gap-2 text-xs flex-wrap">
              <div className="flex-1 min-w-0">
                <span className="font-semibold">{inst.pct}% · {inst.label}</span>
                <span className="text-gray-500"> — {inst.due_at}</span>
                {isLast && <span className="ml-2 text-[10px] bg-purple-100 text-purple-800 px-1.5 py-0.5 rounded font-semibold">EINDFACTUUR</span>}
              </div>
              {isPaid ? (
                <div className="flex items-center gap-1">
                  <span className="text-green-700 font-bold whitespace-nowrap" title={inst.paid_note || ''}>
                    ✓ Betaald{inst.invoice_number ? ` (${inst.invoice_number})` : ''}{inst.paid_via && inst.paid_via !== 'handmatig' ? ` · ${inst.paid_via}` : ''}
                  </span>
                  {!hasInvoice && (
                    <button type="button" onClick={() => unmarkPaid(i)} disabled={busy === `unpaid-${i}`} className="text-red-500 text-[10px] underline hover:text-red-700" data-testid={`unmark-installment-${quote.id}-${i}`}>ongedaan</button>
                  )}
                </div>
              ) : hasInvoice ? (
                <span className="text-blue-700 whitespace-nowrap">Factuur {inst.invoice_number || 'aangemaakt'} (wacht op betaling)</span>
              ) : isLast ? (
                <button
                  type="button"
                  onClick={() => createEindfactuur(i)}
                  disabled={busy === `inv-${i}`}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1 rounded text-[11px] font-semibold disabled:opacity-50 whitespace-nowrap"
                  data-testid={`create-eindfactuur-${quote.id}-${i}`}
                >
                  {busy === `inv-${i}` ? 'Bezig…' : 'Eindfactuur maken'}
                </button>
              ) : (
                <div className="flex items-center gap-1.5 flex-wrap">
                  <button
                    type="button"
                    onClick={() => sendPaymentRequest(i)}
                    disabled={busy === `req-${i}`}
                    className={`${requestSent ? 'bg-sky-500 hover:bg-sky-600' : 'bg-sky-600 hover:bg-sky-700'} text-white px-3 py-1 rounded text-[11px] font-semibold disabled:opacity-50 whitespace-nowrap`}
                    data-testid={`send-payment-request-${quote.id}-${i}`}
                    title="Verstuur een iDEAL-betaal-link per e-mail naar de klant — geen aparte factuur"
                  >
                    {busy === `req-${i}` ? 'Bezig…' : (requestSent ? '↻ Opnieuw versturen' : 'Betalingsverzoek versturen')}
                  </button>
                  <button
                    type="button"
                    onClick={() => markPaid(i)}
                    disabled={busy === `paid-${i}`}
                    className="text-amber-700 hover:text-amber-900 underline text-[10px] disabled:opacity-50 whitespace-nowrap"
                    data-testid={`mark-paid-${quote.id}-${i}`}
                    title="Markeer als al betaald (bv. via bankoverschrijving) — geen e-mail naar klant"
                  >
                    {busy === `paid-${i}` ? '…' : 'of: handmatig markeren'}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <p className="text-[10px] text-gray-500 mt-2 italic">
        Aanbetaling & tussenbetalingen: <strong>geen aparte factuur</strong> — klant ontvangt alleen iDEAL-link per e-mail.
        Alleen de <strong>laatste termijn</strong> wordt een echte factuur (eindfactuur, met aanbetalingen al verrekend).
      </p>
    </div>
  );
};
